import './carriers.scss';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BreadcrumbItem } from '@celebration/react/ClbBreadcrumb/ClbBreadcrumb.types';
import Subheader from '../../common/components/subheader';
import CarriersService from './services/carriers.service';
import CarriersConstants from './constants';
import IPagination from '../../common/interfaces/IPagination';
import ICarriersResponse from './interfaces/ICarriersResponse';
import CarriersFilters from './components/filters';
import ICarriersFiltersModel from './interfaces/ICarriersFiltersModel';
import emptyActiveCarrierFilterModel from './components/filters/constants';
import DeleteModalConfirmation from './components/delete-modal-confirmation';
import ProcessingError from '../../common/errors/processing-error';
import { getCheckedCarriers } from './utils/carriers-utils';
import { disablePageScrollY, enablePageScrollY } from '../../utils/page-utils';
import Toolbar from './components/toolbar';
import CarriersList from './components/carriers-list';
import { useToast } from '../../app/contexts/toast/toast.context';
import Auth from '../../app/contexts/auth';
import BranchesConstants from '../branches/constants';
import ICarrierInfo from '../branches/interfaces/ICarrierInfo';
import HomeConstants from '../home/constants';

export default function Carriers() {
    const { user } = Auth.useAuth();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const { setToast } = useToast();
    const [isCarrierFormOpened, setIsCarrierFormOpened] = useState(false);
    const [renderComponent, setRenderComponent] = useState(true);
    const page = useRef(0);
    const hasMore = useRef(true);
    const [showFilters, setShowFilters] = useState<boolean>();

    const [hasError, setHasError] = useState(false);

    const [carriersList, setCarriersList] = useState<ICarriersResponse[]>([]);
    const carriersFiltersModel = useRef<ICarriersFiltersModel>(
        emptyActiveCarrierFilterModel,
    );
    const [description, setDescription] = useState('');

    const carriersToDelete = useRef<ICarriersResponse[]>([]);
    const [deleteConfirmationModalOpened, setDeleteConfirmationModalOpened] =
        useState(false);
    const [isProcessingDeletion, setIsProcessingDeletion] =
        useState<boolean>(false);

    const [carrierId, setCarrierId] = useState<string | undefined>(undefined);

    const fetchData = useCallback(async () => {
        CarriersService.getCarriersList(
            page.current,
            CarriersConstants.PAGE_SIZE,
            carriersFiltersModel.current,
        )
            .then((resp: IPagination<ICarriersResponse>) => {
                hasMore.current = page.current < resp.totalPages;

                const carriers = getCheckedCarriers(
                    resp.data,
                    carriersToDelete.current,
                );

                setCarriersList(
                    resp.currentPage === 1
                        ? carriers
                        : current => current.concat(carriers),
                );
            })
            .catch((error: Error) => {
                hasMore.current = false;
                setHasError(true);
                setToast('negative', t('Erro'), error.message);
            });
    }, [setToast, t]);

    const fetchNextPage = useCallback(async () => {
        page.current++;

        if (page.current === 1 || hasMore.current) {
            await fetchData();
        }
    }, [fetchData]);

    const fetchFirstPage = useCallback(async () => {
        page.current = 1;
        hasMore.current = true;

        setCarriersList([]);

        fetchData();
    }, [fetchData]);

    const breadcrumbList: BreadcrumbItem[] = [
        { label: HomeConstants.PAGE_TITLE, url: HomeConstants.PATH },
        {
            label: t(
                user?.type === 'third'
                    ? t(CarriersConstants.PAGE_TITLE_SINGULAR)
                    : t(CarriersConstants.PAGE_TITLE),
            ),
            url: CarriersConstants.PATH,
        },
    ];

    useEffect(() => {
        if (user?.type === 'third') {
            navigate(BranchesConstants.PATH, {
                state: {
                    carrierId: user?.carrier?.id,
                    carrierName: user?.carrier?.name,
                } as ICarrierInfo,
            });
        } else if (page.current === 0) {
            fetchFirstPage();
        }
    }, [fetchFirstPage, navigate, user?.carrier, user?.type]);

    useEffect(() => {
        if (description !== undefined) {
            const delayDebounceFn = setTimeout(() => {
                carriersFiltersModel.current = {
                    ...carriersFiltersModel.current,
                    description,
                };
                carriersToDelete.current = [];
                fetchFirstPage();
            }, 300);
            return () => clearTimeout(delayDebounceFn);
        }
    }, [description, fetchFirstPage]);

    useEffect(() => {
        if (isCarrierFormOpened) {
            setRenderComponent(false);
        } else {
            setRenderComponent(true);
        }
    }, [isCarrierFormOpened]);

    const toggleShowFilters = useCallback(() => {
        setShowFilters(!showFilters);
    }, [showFilters]);

    const onApplyFilters = useCallback(
        (filtersModel: ICarriersFiltersModel) => {
            carriersFiltersModel.current = {
                ...filtersModel,
                description,
            };
            carriersToDelete.current = [];
            toggleShowFilters();
            fetchFirstPage();
        },
        [toggleShowFilters, fetchFirstPage, description],
    );

    const handleCarrierCheckbox = useCallback(
        (carrierId: string, event: Event) => {
            const { checked } = event?.target as HTMLInputElement;

            const carriersSelection = carriersList;

            const carrier = carriersSelection.find(c => c.id === carrierId);
            const carrierIdx = carriersSelection.findIndex(
                c => c.id === carrierId,
            );

            if (carrier) {
                carrier.checked = checked;
                carriersSelection[carrierIdx] = carrier;
                setCarriersList([...carriersSelection]);
            }
        },
        [carriersList],
    );

    const checkedCarriers = useMemo(() => {
        return carriersList.filter(c => c.checked);
    }, [carriersList]);

    const anyCarrierChecked = useMemo(() => {
        return checkedCarriers.length > 0;
    }, [checkedCarriers]);

    const deleteCarrier = useCallback(
        (carrierId: string) => {
            const carrierToDeleteInArray = carriersList.filter(
                c => c.id === carrierId,
            );
            carriersToDelete.current = [...carrierToDeleteInArray];

            openDeleteConfirmationModal();
        },
        [carriersList],
    );

    const editCarrier = useCallback((carrierId: string) => {
        setCarrierId(carrierId);
        setIsCarrierFormOpened(true);
    }, []);

    const deleteCarriers = () => {
        carriersToDelete.current = [...checkedCarriers];
        openDeleteConfirmationModal();
    };

    const openDeleteConfirmationModal = () => {
        disablePageScrollY();
        setDeleteConfirmationModalOpened(true);
    };

    const closeDeleteConfirmationModal = () => {
        setDeleteConfirmationModalOpened(false);
        enablePageScrollY();
    };

    const submitCarriersDelete = async () => {
        setIsProcessingDeletion(true);

        try {
            await CarriersService.deleteCarriers(carriersToDelete.current);

            setToast(
                'positive',
                carriersToDelete.current.length === 1
                    ? t('TransportadoraExcluida')
                    : t('TransportadorasExcluidas'),
            );

            fetchFirstPage();
        } catch (error) {
            if (error instanceof ProcessingError) {
                setToast(
                    'warning',
                    t('TransportadorasNaoExcluidas'),
                    error.message,
                );

                fetchFirstPage();
            } else if (error instanceof Error) {
                setToast(
                    'negative',
                    carriersToDelete.current.length === 1
                        ? t('TransportadoraNaoPodeExcluida')
                        : t('TransportadorasNaoPodeExcluidas'),
                    error.message,
                );
            }
        } finally {
            closeDeleteConfirmationModal();
            setIsProcessingDeletion(false);
        }
    };

    return (
        <>
            <Subheader
                title={t(CarriersConstants.PAGE_TITLE)}
                breadcrumbList={breadcrumbList}
                dataTestid="carrier-subheader"
            />

            <div className="carriers-content">
                <Toolbar
                    anyCarrierChecked={anyCarrierChecked}
                    description={description}
                    setDescriptionCallback={setDescription}
                    toggleShowFiltersCallback={toggleShowFilters}
                    confirmCarriersDeleteCallback={deleteCarriers}
                    isCarrierFormOpened={isCarrierFormOpened}
                    setIsCarrierFormOpened={setIsCarrierFormOpened}
                    setEditCarrierId={setCarrierId}
                    carrierId={carrierId}
                />

                {renderComponent && (
                    <CarriersList
                        carriersList={carriersList}
                        fetchNextPageCallback={fetchNextPage}
                        handleCarrierCheckboxCallback={handleCarrierCheckbox}
                        deleteCarrierCallback={deleteCarrier}
                        editCarrierCallback={editCarrier}
                        hasMoreRef={hasMore}
                        hasError={hasError}
                    />
                )}
            </div>

            {renderComponent && showFilters && (
                <CarriersFilters
                    isShowingFilters={showFilters}
                    currentFilters={carriersFiltersModel.current}
                    toggleShowFilters={toggleShowFilters}
                    onApplyFilter={onApplyFilters}
                />
            )}

            {renderComponent && deleteConfirmationModalOpened && (
                <DeleteModalConfirmation
                    opened={deleteConfirmationModalOpened}
                    isProcessingDeletion={isProcessingDeletion}
                    carriersToDelete={carriersToDelete.current}
                    closeModalCallback={closeDeleteConfirmationModal}
                    deleteConfirmedCallback={submitCarriersDelete}
                />
            )}
        </>
    );
}
